const SHOW_AFTER_MS = 500;
const MIN_VISIBLE_MS = 2000;
const FADE_OUT_MS = 180;
const LOADER_ID = 'iso-startup-loader';

type Listener = () => void;

let startupComplete = false;
let startupPhaseActive = true;
let loaderVisible = false;

let showTimer: number | undefined;
let hideTimer: number | undefined;
let shownAt = 0;

const listeners = new Set<Listener>();

function getLoader(): HTMLElement | null {
  return document.getElementById(LOADER_ID);
}

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function getIsStartupPhaseActive(): boolean {
  return startupPhaseActive;
}

export function subscribeToStartupPhase(listener: Listener): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

/**
 * Call this once, immediately at startup (first lines of index.tsx).
 */
export function beginStartupLoading(): void {
  if (showTimer || !startupPhaseActive) {
    return;
  }

  showTimer = window.setTimeout(() => {
    showTimer = undefined;

    // At t0 + SHOW_AFTER_MS:
    // show only if startup loading is still not complete.
    if (startupComplete || !startupPhaseActive) {
      return;
    }

    const loader = getLoader();
    if (!loader) {
      return;
    }

    loaderVisible = true;
    shownAt = Date.now();
    loader.setAttribute('data-visible', 'true');
    loader.removeAttribute('data-exiting');
  }, SHOW_AFTER_MS);
}

/**
 * Call this exactly when startup loading is truly done.
 */
export function completeStartupLoading(): void {
  if (startupComplete) {
    return;
  }

  startupComplete = true;

  if (showTimer) {
    window.clearTimeout(showTimer);
    showTimer = undefined;
  }

  const loader = getLoader();

  // If it never became visible, startup phase ends immediately.
  if (!loaderVisible || !loader) {
    finishStartupPhase();

    return;
  }

  const visibleFor = Date.now() - shownAt;
  const remainingVisibleMs = Math.max(0, MIN_VISIBLE_MS - visibleFor);

  hideTimer = window.setTimeout(() => {
    loader.removeAttribute('data-visible');
    loader.setAttribute('data-exiting', 'true');

    window.setTimeout(() => {
      finishStartupPhase();
    }, FADE_OUT_MS);
  }, remainingVisibleMs);
}

function finishStartupPhase(): void {
  if (!startupPhaseActive) {
    return;
  }

  startupPhaseActive = false;

  if (hideTimer) {
    window.clearTimeout(hideTimer);
    hideTimer = undefined;
  }

  const loader = getLoader();
  loader?.remove();

  notify();
}
